import { format } from 'date-fns'
import { CalendarIcon, Filter } from 'lucide-react'
import { FC } from 'react'

import apps, { App } from '@/apps'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { GlobalSearchFilters } from '@/hooks/useGlobalSearch'
import { cn } from '@/lib/utils'

interface SearchFiltersProps {
  filters: GlobalSearchFilters
  setFilters: (filters: GlobalSearchFilters) => void
  children?: React.ReactNode
}

const SearchFilters: FC<SearchFiltersProps> = ({ filters, setFilters, children }) => {
  const handleReset = () => {
    setFilters({})
  }

  const handleAppChange = (value: string) => {
    setFilters({
      ...filters,
      app: value === 'all' ? null : (value as App),
    })
  }

  const handleStatusChange = (value: string) => {
    setFilters({
      ...filters,
      status: value === 'all' ? null : (value as 'success' | 'failure' | 'timeout'),
    })
  }

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    setFilters({
      ...filters,
      dateRange: range && range.from ? range : null,
    })
  }

  return (
    <div className="flex items-center w-full gap-2 px-4 py-2 overflow-visible border-b bg-muted/30">
      <div className="flex items-center mr-2">
        <Filter className="w-3 h-3 mr-2 text-primary" />
        <span className="text-xs font-medium">Filters:</span>
      </div>
      <>
        <Select value={filters.app || 'all'} onValueChange={handleAppChange}>
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <SelectValue placeholder="All apps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All apps</SelectItem>
            {apps.map(app => (
              <SelectItem key={app.id} value={app.id}>
                <span className="flex items-center gap-2">
                  <app.icon className={`w-4 h-4 text-${app.color}-500`} />
                  {app.title}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.status || 'all'} onValueChange={handleStatusChange}>
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="success">Successful</SelectItem>
            <SelectItem value="failure">Failed</SelectItem>
            <SelectItem value="timeout">Timed out</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn('h-8 border-dashed justify-start text-xs', !filters.dateRange && 'text-muted-foreground')}
            >
              <CalendarIcon className="mr-2 h-3.5 w-3.5" />
              {filters.dateRange?.from ? (
                filters.dateRange.to ? (
                  <>
                    {format(filters.dateRange.from, 'LLL dd, y')} - {format(filters.dateRange.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(filters.dateRange.from, 'LLL dd, y')
                )
              ) : (
                'Date Range'
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={filters.dateRange?.from}
              selected={{
                from: filters.dateRange?.from,
                to: filters.dateRange?.to,
              }}
              onSelect={handleDateRangeChange}
            />
          </PopoverContent>
        </Popover>

        {(filters.app || filters.status || filters.dateRange) && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleReset}>
            Reset
          </Button>
        )}
      </>
      <div className="flex items-center justify-end flex-1">{children}</div>
    </div>
  )
}

export default SearchFilters
